import { CrownOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Dropdown, Menu, Popconfirm, Select, Tooltip, Typography, message } from 'antd'
import cls from 'classnames'
import { sortBy, xor } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserAvatar, UserName, getPointPermission } from '@/components'
import { CustomerRole } from '@/modules/customer/customer.schema'
import { DevlopRole } from '@/modules/devlop/devlop.schema'
import { PortfolioRole } from '@/modules/portfolio/portfolio.schema'
import { ProductionRole } from '@/modules/production-project/production.schema'
import { ProjectRole } from '@/modules/project/project.schema'
import { PAGE_RESOURCE_POINTS_ENUM, ResourcePermission, ResourceType, ResourceUser, TeamRole } from '@/schemas'

interface ResourceUserProps extends ResourcePermission {
  selfId: string
  user: ResourceUser
  roleOptions: { label: string; value: string }[]
  onChange: () => void
  resourceType: ResourceType
}

export const getSortWeight = (role: string) => {
  if (role === '负责人') return 0
  if (role.toLowerCase() === 'to') return 2
  return 3
}

export const ResourceUserContainer: React.FC<ResourceUserProps> = ({
  selfId,
  user,
  permissions,
  resourceType,
  roleOptions,
  onChange,
}) => {
  const { t } = useTranslation()
  const excludeOwner = (role: string) => (resourceType === ResourceType.TEAM ? true : role !== '负责人')

  const { canDelete, canUpdate, editable } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_成员, permissions)
  const {
    canUpdate: canUpdateALL,
    canCreate: canCreateALL,
    canDelete: canDeleteALL,
  } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.全部, user.permissions)

  const [editingRoles, setEditingRoles] = React.useState(false)

  const getNames = () => sortBy(user.roles, getSortWeight)
  const [names, setNames] = React.useState(getNames)

  React.useEffect(() => {
    setNames(getNames())
  }, [user.roles])

  const { run: deleteUser } = useRequest(
    {
      url: '/api/userRoles/deleteResourceUser',
      method: 'delete',
      params: {
        selfId,
        email: user.email,
        type: resourceType,
      },
    },
    {
      manual: true,
      onSuccess: () => onChange(),
    }
  )

  const { run: updateUserRoles } = useRequest(
    {
      url: '/api/userRoles/updateResourceUserRoles',
      method: 'post',
      data: {
        selfId,
        email: user.email,
        roles: names.filter(excludeOwner),
        type: resourceType,
      },
    },
    {
      manual: true,
      onSuccess: () => {
        onChange()
        setEditingRoles(false)
      },
    }
  )

  const removeUser = () => {
    deleteUser()
  }

  const menu = (
    <Menu>
      {canUpdate && (
        <Menu.Item key='edit' icon={<EditOutlined />} onClick={() => setEditingRoles(true)}>
          {t('update roles')}
        </Menu.Item>
      )}
      {canDelete && (
        <Popconfirm
          placement='top'
          title={`${t('remove')}${t('users')} ${user.email}？`}
          okText={t('remove')}
          onConfirm={removeUser}
        >
          <Menu.Item key='delete' icon={<DeleteOutlined />}>
            {t('remove')}
          </Menu.Item>
        </Popconfirm>
      )}
    </Menu>
  )

  const updateRoles = async () => {
    updateUserRoles()
  }

  const userRoles = editingRoles ? (
    <Select
      autoFocus
      size='small'
      mode={
        [
          ResourceType.CUSTOMER,
          ResourceType.PRODUCTION,
          ResourceType.DEVLOP,
          ResourceType.PROJECT,
          ResourceType.PORTFOLIO,
        ].includes(resourceType)
          ? 'multiple'
          : 'tags'
      }
      placeholder={t('write role name')}
      allowClear={true}
      open={true}
      style={{ width: '100%', minWidth: 150 }}
      options={roleOptions
        .filter((r) => excludeOwner(r.value))
        .filter((v) => {
          return resourceType === ResourceType.CUSTOMER
            ? Object.values(CustomerRole).includes(v.label as any)
            : resourceType === ResourceType.PRODUCTION
              ? Object.values(ProductionRole).includes(v.label as any)
              : resourceType === ResourceType.DEVLOP
                ? Object.values(DevlopRole).includes(v.label as any)
                : resourceType === ResourceType.PROJECT
                  ? Object.values(ProjectRole).includes(v.label as any)
                  : resourceType === ResourceType.PORTFOLIO
                    ? Object.values(PortfolioRole).includes(v.label as any)
                    : resourceType === ResourceType.TEAM
                      ? Object.values(TeamRole).includes(v.label as any)
                      : true
        })}
      value={names}
      onChange={(names) => setNames(names)}
      onBlur={async () => {
        const isOwner = user.roles.includes('负责人')
        if (!isOwner && names.length === 0) {
          message.warn(t('must have one'))
          setNames(getNames())
          setEditingRoles(false)
        } else if (xor(user.roles, names).length > 0) {
          updateRoles()
        } else {
          setEditingRoles(false)
        }
      }}
    />
  ) : (
    <Typography.Text type='secondary' className='block'>
      {names.join(',')}
    </Typography.Text>
  )

  // 仅拥有所有增删改权限的则显示皇冠
  const crown = canUpdateALL && canCreateALL && canDeleteALL && (
    <Tooltip title={t('admin')}>
      <CrownOutlined />
    </Tooltip>
  )

  return (
    <Dropdown overlay={menu} trigger={editingRoles || !editable ? [] : ['click']} disabled={!editable}>
      <div
        className={cls('flex items-center select-none', {
          'cursor-pointer': editable,
        })}
      >
        <UserAvatar className='flex-shrink-0 mr-8px' email={user.email} />
        <div className='text-xs'>
          <Typography.Text className='space-x-1 block'>
            <UserName email={user.email} />
            {crown}
          </Typography.Text>
          {userRoles}
        </div>
      </div>
    </Dropdown>
  )
}
